import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Tag, Menu, FileText, Search, Plus, Trash2, 
  Edit2, RefreshCw, GripVertical, ChevronDown, ChevronUp,
  Twitter, Linkedin, Instagram, Facebook, Youtube, Globe,
  Save, RotateCcw, Sparkles, Link, ExternalLink, Send
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import * as api from '../../services/api';

// ======================= MAIN CONFIG MANAGER =======================
const SiteConfigManager = () => {
  const [activeSection, setActiveSection] = useState('categories');
  
  const sections = [
    { id: 'categories', label: 'Categorías', icon: Tag },
    { id: 'navigation', label: 'Navegación', icon: Menu },
    { id: 'footer', label: 'Footer', icon: FileText },
    { id: 'seo', label: 'SEO', icon: Search },
  ];
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Settings className="w-5 h-5" />
        <h2 className="text-xl font-bold">Configuración del Sitio</h2>
      </div>
      
      {/* Section Tabs */}
      <div className="flex gap-2 border-b pb-2 overflow-x-auto">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <Button
              key={section.id}
              variant={activeSection === section.id ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveSection(section.id)}
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <Icon className="w-4 h-4" />
              {section.label}
            </Button>
          );
        })}
      </div>
      
      {/* Section Content */}
      <div className="mt-4">
        {activeSection === 'categories' && <CategoriesConfig />}
        {activeSection === 'navigation' && <NavigationConfig />}
        {activeSection === 'footer' && <FooterConfig />}
        {activeSection === 'seo' && <SEOConfig />}
      </div>
    </div>
  );
};

// ======================= CATEGORIES CONFIG =======================
const CategoriesConfig = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.getSiteConfig('categories');
      setCategories(res.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSiteConfig('categories', { data: categories });
      alert('Categorías guardadas');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = async () => {
    if (!window.confirm('¿Restaurar categorías por defecto?')) return;
    setSaving(true);
    try {
      await api.resetSiteConfig('categories');
      await fetchCategories();
      alert('Categorías restauradas');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async (categoryId) => {
    if (!window.confirm('¿Eliminar esta categoría?')) return;
    try {
      await api.deleteCategory(categoryId);
      await fetchCategories();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleAddCategory = async (newCategory) => {
    try {
      await api.addCategory(newCategory);
      await fetchCategories();
      setShowAddModal(false);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const updateCategory = (index, field, value) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], [field]: value };
    setCategories(updated);
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Gestiona las categorías/sectores de las noticias
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-1" />
            Restaurar
          </Button>
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Nueva Categoría
          </Button>
        </div>
      </div>
      
      <div className="space-y-2">
        {categories.map((cat, index) => (
          <div key={cat.id || index} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
            <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
            
            <div 
              className="w-6 h-6 rounded-full flex-shrink-0 border-2"
              style={{ backgroundColor: cat.color || '#000' }}
            />
            
            <Input
              value={cat.name}
              onChange={(e) => updateCategory(index, 'name', e.target.value)}
              className="w-32"
              placeholder="Nombre"
            />
            
            <Input
              value={cat.slug}
              onChange={(e) => updateCategory(index, 'slug', e.target.value)}
              className="w-28 text-sm"
              placeholder="slug"
            />
            
            <Input
              type="color"
              value={cat.color || '#000000'}
              onChange={(e) => updateCategory(index, 'color', e.target.value)}
              className="w-12 h-8 p-0 border-0"
            />
            
            <Input
              value={cat.description || ''}
              onChange={(e) => updateCategory(index, 'description', e.target.value)}
              className="flex-1 text-sm"
              placeholder="Descripción"
            />
            
            <label className="flex items-center gap-1 text-sm">
              <input
                type="checkbox"
                checked={cat.is_active !== false}
                onChange={(e) => updateCategory(index, 'is_active', e.target.checked)}
              />
              Activa
            </label>
            
            <Button variant="ghost" size="sm" onClick={() => handleDelete(cat.id)}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
      
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>
      
      {/* Add Category Modal */}
      {showAddModal && (
        <CategoryModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCategory}
        />
      )}
    </div>
  );
};

const CategoryModal = ({ category, onClose, onSave }) => {
  const [form, setForm] = useState(category || {
    name: '',
    slug: '',
    description: '',
    color: '#000000',
    order: 0,
    is_active: true,
  });
  
  const handleSubmit = () => {
    if (!form.name || !form.slug) {
      alert('Nombre y slug son requeridos');
      return;
    }
    onSave({ ...form, id: form.slug });
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{category ? 'Editar Categoría' : 'Nueva Categoría'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Nombre</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ej: Tecnología"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Slug (URL)</label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              placeholder="Ej: tecnologia"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Descripción</label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descripción breve"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Color</label>
            <div className="flex items-center gap-2">
              <Input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="w-16 h-10"
              />
              <Input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                placeholder="#000000"
                className="w-28"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ======================= NAVIGATION CONFIG =======================
const NavigationConfig = () => {
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  const fetchNavigation = useCallback(async () => {
    try {
      const res = await api.getSiteConfig('navigation');
      setNavigation(res.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchNavigation();
  }, [fetchNavigation]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSiteConfig('navigation', { data: navigation });
      alert('Navegación guardada');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = async () => {
    if (!window.confirm('¿Restaurar navegación por defecto?')) return;
    setSaving(true);
    try {
      await api.resetSiteConfig('navigation');
      await fetchNavigation();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const updateNavItem = (index, field, value) => {
    const updated = [...navigation];
    updated[index] = { ...updated[index], [field]: value };
    setNavigation(updated);
  };
  
  const deleteNavItem = (index) => {
    if (!window.confirm('¿Eliminar este elemento?')) return;
    setNavigation(navigation.filter((_, i) => i !== index));
  };
  
  const addNavItem = (item) => {
    setNavigation([...navigation, { ...item, id: `nav_${Date.now()}`, order: navigation.length + 1 }]);
    setShowAddModal(false);
  };
  
  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= navigation.length) return;
    const updated = [...navigation];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    // Update order
    updated.forEach((item, i) => item.order = i + 1);
    setNavigation(updated);
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Configura los elementos del menú de navegación
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-1" />
            Restaurar
          </Button>
          <Button size="sm" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1" />
            Nuevo Enlace
          </Button>
        </div>
      </div>
      
      <div className="space-y-2">
        {navigation.sort((a, b) => a.order - b.order).map((item, index) => (
          <div key={item.id || index} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
            <div className="flex flex-col gap-1">
              <button onClick={() => moveItem(index, -1)} className="p-1 hover:bg-gray-100 rounded">
                <ChevronUp className="w-3 h-3" />
              </button>
              <button onClick={() => moveItem(index, 1)} className="p-1 hover:bg-gray-100 rounded">
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
            
            <Badge variant="outline" className="w-6 text-center">{item.order}</Badge>
            
            <Input
              value={item.label}
              onChange={(e) => updateNavItem(index, 'label', e.target.value)}
              className="w-40"
              placeholder="Etiqueta"
            />
            
            <Input
              value={item.url}
              onChange={(e) => updateNavItem(index, 'url', e.target.value)}
              className="flex-1"
              placeholder="/ruta"
            />
            
            <label className="flex items-center gap-1 text-sm whitespace-nowrap">
              <input
                type="checkbox"
                checked={item.is_active !== false}
                onChange={(e) => updateNavItem(index, 'is_active', e.target.checked)}
              />
              Visible
            </label>
            
            <Button variant="ghost" size="sm" onClick={() => deleteNavItem(index)}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
      
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>
      
      {/* Add Nav Item Modal */}
      {showAddModal && (
        <Dialog open={true} onOpenChange={() => setShowAddModal(false)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo Elemento de Navegación</DialogTitle>
            </DialogHeader>
            <NavItemForm onSave={addNavItem} onCancel={() => setShowAddModal(false)} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const NavItemForm = ({ item, onSave, onCancel }) => {
  const [form, setForm] = useState(item || { label: '', url: '', is_active: true });
  
  return (
    <>
      <div className="space-y-4">
        <div>
          <label className="text-sm font-medium">Etiqueta</label>
          <Input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="Ej: Contacto"
          />
        </div>
        <div>
          <label className="text-sm font-medium">URL</label>
          <Input
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="Ej: /contacto o https://..."
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={() => onSave(form)}>Agregar</Button>
      </DialogFooter>
    </>
  );
};

// ======================= FOOTER CONFIG =======================
const FooterConfig = () => {
  const [footer, setFooter] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editingLinkIndex, setEditingLinkIndex] = useState(null);
  const [suggestingLinks, setSuggestingLinks] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [showAiInput, setShowAiInput] = useState(false);
  
  const fetchFooter = useCallback(async () => {
    try {
      const res = await api.getSiteConfig('footer');
      setFooter(res.data || {});
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchFooter();
  }, [fetchFooter]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSiteConfig('footer', { data: footer });
      alert('Footer guardado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = async () => {
    if (!window.confirm('¿Restaurar footer por defecto?')) return;
    setSaving(true);
    try {
      await api.resetSiteConfig('footer');
      await fetchFooter();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const updateSocialLink = (index, field, value) => {
    const links = [...(footer.social_links || [])];
    links[index] = { ...links[index], [field]: value };
    setFooter({ ...footer, social_links: links });
  };
  
  const addSocialLink = () => {
    const links = [...(footer.social_links || [])];
    links.push({ platform: 'twitter', url: '', icon: 'twitter' });
    setFooter({ ...footer, social_links: links });
  };
  
  const deleteSocialLink = (index) => {
    const links = footer.social_links.filter((_, i) => i !== index);
    setFooter({ ...footer, social_links: links });
  };
  
  // Footer Links CRUD
  const openLinkEditor = (link = null, index = null) => {
    if (link) {
      setEditingLink({ ...link });
      setEditingLinkIndex(index);
    } else {
      setEditingLink({ label: '', url: '', section: 'company', description: '' });
      setEditingLinkIndex(null);
    }
  };
  
  const saveLinkEdit = () => {
    if (!editingLink.label || !editingLink.url) {
      alert('Etiqueta y URL son requeridos');
      return;
    }
    
    const links = [...(footer.footer_links || [])];
    if (editingLinkIndex !== null) {
      links[editingLinkIndex] = editingLink;
    } else {
      links.push(editingLink);
    }
    setFooter({ ...footer, footer_links: links });
    setEditingLink(null);
    setEditingLinkIndex(null);
  };
  
  const deleteFooterLink = (index) => {
    if (!window.confirm('¿Eliminar este enlace?')) return;
    const links = footer.footer_links.filter((_, i) => i !== index);
    setFooter({ ...footer, footer_links: links });
  };
  
  // AI Suggestions for footer links
  const suggestFooterLinks = async () => {
    setSuggestingLinks(true);
    try {
      const res = await api.suggestFooterLinks({
        instruction: aiInstruction,
        company_name: footer.company_name || 'Influencia & Poder',
        current_links: footer.footer_links || []
      });
      
      if (res.success && res.suggestions) {
        // Parse suggestions and add to footer_links
        const newLinks = res.suggestions.map(s => ({
          label: s.label,
          url: s.url,
          section: s.section || 'company',
          description: s.description || ''
        }));
        
        const existingLinks = footer.footer_links || [];
        setFooter({ 
          ...footer, 
          footer_links: [...existingLinks, ...newLinks] 
        });
        setShowAiInput(false);
        setAiInstruction('');
      }
    } catch (error) {
      alert('Error al generar sugerencias: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSuggestingLinks(false);
    }
  };
  
  const socialIcons = {
    twitter: Twitter,
    linkedin: Linkedin,
    instagram: Instagram,
    facebook: Facebook,
    youtube: Youtube,
    website: Globe,
  };
  
  const sectionLabels = {
    company: 'Empresa',
    legal: 'Legal',
    resources: 'Recursos',
    support: 'Soporte',
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Configura la información del footer
        </p>
        <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-4 h-4 mr-1" />
          Restaurar
        </Button>
      </div>
      
      {/* Basic Info */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <h3 className="font-medium">Información General</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Nombre de la Empresa</label>
            <Input
              value={footer.company_name || ''}
              onChange={(e) => setFooter({ ...footer, company_name: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Tagline</label>
            <Input
              value={footer.tagline || ''}
              onChange={(e) => setFooter({ ...footer, tagline: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Texto de Copyright</label>
            <Input
              value={footer.copyright_text || ''}
              onChange={(e) => setFooter({ ...footer, copyright_text: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Email de Contacto</label>
            <Input
              value={footer.contact_email || ''}
              onChange={(e) => setFooter({ ...footer, contact_email: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Teléfono</label>
            <Input
              value={footer.contact_phone || ''}
              onChange={(e) => setFooter({ ...footer, contact_phone: e.target.value })}
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Dirección</label>
            <Input
              value={footer.address || ''}
              onChange={(e) => setFooter({ ...footer, address: e.target.value })}
            />
          </div>
        </div>
      </div>
      
      {/* Social Links */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Redes Sociales</h3>
          <Button size="sm" variant="outline" onClick={addSocialLink}>
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        </div>
        <div className="space-y-2">
          {(footer.social_links || []).map((link, index) => {
            const Icon = socialIcons[link.platform] || Globe;
            return (
              <div key={index} className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-gray-500" />
                <select
                  value={link.platform}
                  onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="twitter">Twitter/X</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="youtube">YouTube</option>
                  <option value="website">Web</option>
                </select>
                <Input
                  value={link.url}
                  onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                  className="flex-1"
                  placeholder="https://..."
                />
                <Button variant="ghost" size="sm" onClick={() => deleteSocialLink(index)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Footer Links - Enhanced */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-medium flex items-center gap-2">
            <Link className="w-4 h-4" />
            Enlaces del Footer
            <Badge variant="outline" className="ml-2">
              {(footer.footer_links || []).length} enlaces
            </Badge>
          </h3>
          <div className="flex gap-2">
            {/* AI Suggest Button */}
            <Button 
              size="sm" 
              variant="outline" 
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
              onClick={() => setShowAiInput(!showAiInput)}
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Sugerir con IA
            </Button>
            <Button size="sm" variant="outline" onClick={() => openLinkEditor()}>
              <Plus className="w-4 h-4 mr-1" /> Nuevo Enlace
            </Button>
          </div>
        </div>
        
        {/* AI Instruction Input */}
        {showAiInput && (
          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
            <label className="text-sm font-medium text-purple-700 block mb-2">
              Instrucciones para la IA (opcional)
            </label>
            <div className="flex gap-2">
              <Input
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder="Ej: Agregar enlaces legales, página de contacto, FAQ..."
                className="flex-1"
                onKeyPress={(e) => e.key === 'Enter' && suggestFooterLinks()}
              />
              <Button 
                onClick={suggestFooterLinks} 
                disabled={suggestingLinks}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {suggestingLinks ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowAiInput(false)}>
                ×
              </Button>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              La IA sugerirá enlaces relevantes para un medio de noticias basándose en tus instrucciones.
            </p>
          </div>
        )}
        
        {/* Links organized by section */}
        {['company', 'legal', 'resources', 'support'].map(section => {
          const sectionLinks = (footer.footer_links || []).filter(l => l.section === section);
          if (sectionLinks.length === 0) return null;
          
          return (
            <div key={section} className="space-y-2">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                {sectionLabels[section]}
              </h4>
              <div className="space-y-1">
                {sectionLinks.map((link) => {
                  const originalIndex = (footer.footer_links || []).findIndex(l => l === link);
                  return (
                    <div 
                      key={originalIndex} 
                      className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 group"
                    >
                      <div className="flex items-center gap-3">
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                        <div>
                          <span className="font-medium text-sm">{link.label}</span>
                          <span className="text-xs text-gray-400 ml-2">{link.url}</span>
                          {link.description && (
                            <p className="text-xs text-gray-500">{link.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openLinkEditor(link, originalIndex)}
                        >
                          <Edit2 className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteFooterLink(originalIndex)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        
        {(footer.footer_links || []).length === 0 && (
          <div className="text-center py-6 text-gray-400">
            <Link className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay enlaces configurados</p>
            <p className="text-xs">Usa &quot;Sugerir con IA&quot; o &quot;Nuevo Enlace&quot; para agregar</p>
          </div>
        )}
      </div>
      
      {/* Link Editor Modal */}
      {editingLink && (
        <Dialog open={true} onOpenChange={() => setEditingLink(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link className="w-5 h-5" />
                {editingLinkIndex !== null ? 'Editar Enlace' : 'Nuevo Enlace'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Sección *</label>
                <select
                  value={editingLink.section}
                  onChange={(e) => setEditingLink({ ...editingLink, section: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm mt-1"
                >
                  <option value="company">Empresa</option>
                  <option value="legal">Legal</option>
                  <option value="resources">Recursos</option>
                  <option value="support">Soporte</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Etiqueta *</label>
                <Input
                  value={editingLink.label}
                  onChange={(e) => setEditingLink({ ...editingLink, label: e.target.value })}
                  placeholder="Ej: Sobre Nosotros"
                />
              </div>
              <div>
                <label className="text-sm font-medium">URL *</label>
                <Input
                  value={editingLink.url}
                  onChange={(e) => setEditingLink({ ...editingLink, url: e.target.value })}
                  placeholder="/about o https://..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Descripción (opcional)</label>
                <Textarea
                  value={editingLink.description || ''}
                  onChange={(e) => setEditingLink({ ...editingLink, description: e.target.value })}
                  placeholder="Breve descripción del enlace"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingLink(null)}>
                Cancelar
              </Button>
              <Button onClick={saveLinkEdit}>
                <Save className="w-4 h-4 mr-2" />
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>
    </div>
  );
};

// ======================= SEO CONFIG =======================
const SEOConfig = () => {
  const [seo, setSeo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newKeyword, setNewKeyword] = useState('');
  
  const fetchSeo = useCallback(async () => {
    try {
      const res = await api.getSiteConfig('seo');
      setSeo(res.data || {});
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchSeo();
  }, [fetchSeo]);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateSiteConfig('seo', { data: seo });
      alert('SEO guardado');
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const handleReset = async () => {
    if (!window.confirm('¿Restaurar SEO por defecto?')) return;
    setSaving(true);
    try {
      await api.resetSiteConfig('seo');
      await fetchSeo();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };
  
  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    const keywords = [...(seo.meta_keywords || []), newKeyword.trim()];
    setSeo({ ...seo, meta_keywords: keywords });
    setNewKeyword('');
  };
  
  const removeKeyword = (index) => {
    const keywords = seo.meta_keywords.filter((_, i) => i !== index);
    setSeo({ ...seo, meta_keywords: keywords });
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Configura el SEO y meta tags del sitio
        </p>
        <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
          <RotateCcw className="w-4 h-4 mr-1" />
          Restaurar
        </Button>
      </div>
      
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <h3 className="font-medium">Meta Tags Principales</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Título del Sitio</label>
            <Input
              value={seo.site_title || ''}
              onChange={(e) => setSeo({ ...seo, site_title: e.target.value })}
              placeholder="Título principal del sitio"
            />
            <p className="text-xs text-gray-500 mt-1">Aparece en la pestaña del navegador</p>
          </div>
          <div>
            <label className="text-sm font-medium">Descripción del Sitio</label>
            <Textarea
              value={seo.site_description || ''}
              onChange={(e) => setSeo({ ...seo, site_description: e.target.value })}
              rows={3}
              placeholder="Descripción para motores de búsqueda"
            />
            <p className="text-xs text-gray-500 mt-1">Máximo 160 caracteres recomendado</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <h3 className="font-medium">Palabras Clave</h3>
        <div className="flex gap-2">
          <Input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder="Nueva palabra clave"
            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
          />
          <Button onClick={addKeyword}>Agregar</Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {(seo.meta_keywords || []).map((keyword, index) => (
            <Badge key={index} variant="secondary" className="flex items-center gap-1">
              {keyword}
              <button onClick={() => removeKeyword(index)} className="ml-1 hover:text-red-500">×</button>
            </Badge>
          ))}
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <h3 className="font-medium">Redes Sociales / Open Graph</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Twitter Handle</label>
            <Input
              value={seo.twitter_handle || ''}
              onChange={(e) => setSeo({ ...seo, twitter_handle: e.target.value })}
              placeholder="@usuario"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Imagen OG (compartir)</label>
            <Input
              value={seo.og_image || ''}
              onChange={(e) => setSeo({ ...seo, og_image: e.target.value })}
              placeholder="/og-image.jpg"
            />
          </div>
        </div>
      </div>
      
      <div className="bg-white p-4 rounded-lg border space-y-4">
        <h3 className="font-medium">Analytics</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Google Analytics ID</label>
            <Input
              value={seo.google_analytics_id || ''}
              onChange={(e) => setSeo({ ...seo, google_analytics_id: e.target.value })}
              placeholder="G-XXXXXXXXXX"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Favicon URL</label>
            <Input
              value={seo.favicon_url || ''}
              onChange={(e) => setSeo({ ...seo, favicon_url: e.target.value })}
              placeholder="/favicon.ico"
            />
          </div>
        </div>
      </div>
      
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>
    </div>
  );
};

export default SiteConfigManager;
